import React from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { PersonPortrait } from './PersonCard';

// A person's full profile, in a panel from the right: portrait and role up
// top, the whole bio, then their appointments as a timeline. Previous and
// next let a visitor read through the roster without closing it.
const ProfileDrawer = ({ member, prev, next, onClose, onGo }) => {
  const size = useBreakpointValue({ base: 'full', md: 'lg' }, { ssr: false });

  return (
    <Drawer isOpen={!!member} placement="right" onClose={onClose} size={size || 'lg'}>
      <DrawerOverlay bg="rgba(14,47,58,0.42)" backdropFilter="blur(3px)" />
      <DrawerContent bg="white" maxW={{ md: '36rem' }}>
        {/* Solid, so body text scrolling under it never shows through. */}
        <DrawerCloseButton
          top={5}
          right={5}
          size="lg"
          zIndex={2}
          borderRadius="full"
          color="ink.700"
          bg="white"
          border="1px solid"
          borderColor="rgba(14,47,58,0.08)"
          boxShadow="0 6px 18px -8px rgba(14,47,58,0.35)"
          _hover={{ bg: 'mist.100' }}
        />
        {member && (
          <>
            <DrawerBody px={{ base: 6, md: 10 }} pt={{ base: 16, md: 14 }} pb={10}>
              <Flex gap={{ base: 5, md: 7 }} align="flex-end">
                <PersonPortrait member={member} w={{ base: '118px', md: '150px' }} flexShrink={0} rounded="16px" />
                <Box minW={0} pb={1}>
                  {member.title && (
                    <Text
                      fontSize="11px"
                      fontWeight="700"
                      color="brand.700"
                      letterSpacing="0.16em"
                      textTransform="uppercase"
                      lineHeight="1.5"
                      mb={2}
                    >
                      {member.title}
                    </Text>
                  )}
                  {member.name && (
                    <Heading
                      as="h2"
                      fontSize={{ base: '1.5rem', md: '1.85rem' }}
                      color="ink.900"
                      fontWeight="600"
                      letterSpacing="-0.025em"
                      lineHeight="1.15"
                    >
                      {member.name}
                    </Heading>
                  )}
                  {member.affiliations && (
                    <Text mt={3} fontSize="sm" color="ink.500" lineHeight="1.55">
                      {member.affiliations.join(' · ')}
                    </Text>
                  )}
                </Box>
              </Flex>

              <Box h="1px" bg="rgba(14,47,58,0.08)" my={{ base: 8, md: 9 }} />

              {member.bio && (
                <Text color="ink.700" fontSize={{ base: 'md', md: '1.0625rem' }} lineHeight="1.8" whiteSpace="pre-line">
                  {member.bio}
                </Text>
              )}

              {/* Appointments as a timeline: a hairline rail with a ring per
                  role, the role in ink, the organization muted, and the dates
                  in a quiet chip. */}
              {member.roles && member.roles.length > 0 && (
                <Box mt={10}>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="0.16em"
                    color="brand.700"
                    mb={6}
                  >
                    {member.rolesHeading || 'Current Roles'}
                  </Text>

                  <VStack align="stretch" spacing={0} position="relative">
                    <Box
                      position="absolute"
                      left="5px"
                      top="12px"
                      bottom="12px"
                      w="1px"
                      bg="rgba(14,47,58,0.12)"
                      pointerEvents="none"
                    />
                    {member.roles.map((r, i) => (
                      <Flex key={i} align="flex-start" gap={5} pb={i === member.roles.length - 1 ? 0 : 6}>
                        <Box
                          flexShrink={0}
                          mt="6px"
                          w="11px"
                          h="11px"
                          borderRadius="full"
                          bg="white"
                          border="2px solid"
                          borderColor="starOfLife.400"
                          zIndex={1}
                        />
                        <Box flex="1" minW={0}>
                          <Text fontWeight="600" color="ink.900" lineHeight="1.45">
                            {r.role}
                          </Text>
                          {r.org && (
                            <Text color="ink.600" fontSize="sm" mt={1}>
                              {r.org}
                            </Text>
                          )}
                          {r.period && (
                            <Text
                              display="inline-block"
                              mt={2}
                              px={3}
                              py={1}
                              borderRadius="full"
                              bg="mist.100"
                              color="ink.700"
                              fontSize="xs"
                              fontWeight="600"
                              letterSpacing="0.02em"
                            >
                              {r.period}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              )}
            </DrawerBody>

            {(prev || next) && (
              <DrawerFooter
                borderTop="1px solid"
                borderColor="rgba(14,47,58,0.08)"
                px={{ base: 6, md: 10 }}
                py={4}
                justifyContent="space-between"
                gap={4}
              >
                <PagerButton person={prev} dir="prev" onGo={onGo} />
                <PagerButton person={next} dir="next" onGo={onGo} />
              </DrawerFooter>
            )}
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

const PagerButton = ({ person, dir, onGo }) => {
  if (!person) return <Box />;
  const isNext = dir === 'next';
  const label = person.name || person.title;
  return (
    <Box
      as="button"
      type="button"
      onClick={() => onGo(person)}
      textAlign={isNext ? 'right' : 'left'}
      minW={0}
      px={3}
      py={2}
      mx={-3}
      borderRadius="12px"
      transition="background 0.3s"
      _hover={{ bg: 'mist.100' }}
      _focusVisible={{ outline: 'none', boxShadow: '0 0 0 3px rgba(21,150,184,0.55)' }}
    >
      <Text fontSize="11px" fontWeight="700" letterSpacing="0.14em" textTransform="uppercase" color="ink.500">
        {isNext ? 'Next' : 'Previous'}
      </Text>
      <HStack spacing={2} justify={isNext ? 'flex-end' : 'flex-start'} color="ink.900" fontWeight="600" fontSize="sm">
        {!isNext && <Icon as={FaArrowLeft} boxSize={3} />}
        <Text noOfLines={1}>{label}</Text>
        {isNext && <Icon as={FaArrowRight} boxSize={3} />}
      </HStack>
    </Box>
  );
};

export default ProfileDrawer;
